import React, { useState, useEffect, useCallback, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import { getPropsForCustomUserFieldInputs } from '../../util/userHelpers';
import { getFieldRegistryEntry, SIGNUP_STEP_GROUP_ORDER } from '../../config/configFieldRegistry';
import { login, authenticationInProgress, signup } from '../../ducks/auth.duck';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import {
  Page,
  LayoutSingleColumn,
  NamedRedirect,
  IconSpinner,
} from '../../components';
import TopbarContainerStyled from '../TopbarContainer/TopbarContainerStyled';
import FooterStyled from '../FooterContainer/FooterStyled';

import AuthLayout from '../../components/AuthPages/AuthLayout';
import LoginCard from '../../components/AuthPages/LoginCard';
import SignupWizard from '../../components/AuthPages/SignupWizard';

// Tailwind styles
import '../../styles/tailwind.css';

export const NewAuthPageComponent = (props: any) => {
  const [mounted, setMounted] = useState(false);
  const [wizardComplete, setWizardComplete] = useState(false);
  // Track whether the user was already authenticated when this page first rendered
  const wasAuthenticatedOnMount = useRef<boolean | null>(null);
  const config = useConfiguration();
  const intl = useIntl();

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    authInProgress,
    currentUser,
    isAuthenticated,
    location,
    params: pathParams,
    loginError,
    signupError,
    scrollingDisabled,
    submitLogin,
    submitSignup,
    tab = 'signup',
  } = props;

  // Redirect target from location state (e.g. user tried to access a protected page)
  const from = location.state?.from || null;

  // User type from route params or navigation state
  const userTypeInPushState = location.state?.userType || null;
  const userType = pathParams?.userType || userTypeInPushState || null;

  // Validate user type against config
  const { userTypes = [], userFields = [] } = config.user;
  const preselectedUserType =
    userTypes.find((conf: any) => conf.userType === userType)?.userType || null;

  // User type can come from route/state (preselected) or be chosen in the form selector
  const [selectedUserType, setSelectedUserType] = useState<string | null>(preselectedUserType);

  // Sync if preselectedUserType changes (e.g. navigating from /signup to /signup/provider-id)
  useEffect(() => {
    if (preselectedUserType) {
      setSelectedUserType(preselectedUserType);
    }
  }, [preselectedUserType]);

  // Get custom fields for the active user type that should display in signup
  const customFieldProps = getPropsForCustomUserFieldInputs(
    userFields,
    selectedUserType,
    true // isSignup
  );

  // Map the filtered field props back to the raw field configs
  // (getPropsForCustomUserFieldInputs returns { key, name, fieldConfig } — we need fieldConfig)
  const extendedDataFields = customFieldProps.map((fp: any) => fp.fieldConfig);

  // Build synthetic field configs for built-in user type fields (displayName, phoneNumber)
  // These come from user-types.json, not user-fields.json
  const userTypeConfig = userTypes.find((ut: any) => ut.userType === selectedUserType);
  const builtInFields: any[] = [];

  if (userTypeConfig) {
    const { defaultUserFields = {}, displayNameSettings, phoneNumberSettings } = userTypeConfig;

    // displayName — top-level profile field (not extended data)
    if (defaultUserFields.displayName && displayNameSettings?.displayInSignUp) {
      const displayNameLabel = intl.formatMessage({ id: 'SignupForm.displayNameLabel' });
      builtInFields.push({
        key: 'displayName',
        scope: 'profile', // special scope — no prefix from addScopePrefix
        schemaType: 'text',
        label: displayNameLabel,
        saveConfig: {
          label: displayNameLabel,
          isRequired: !!displayNameSettings.required,
          displayInSignUp: true,
        },
      });
    }

    // phoneNumber — stored in protectedData
    if (defaultUserFields.phoneNumber && phoneNumberSettings?.displayInSignUp) {
      const phoneNumberLabel = intl.formatMessage({ id: 'SignupForm.phoneNumberLabel' });
      builtInFields.push({
        key: 'phoneNumber',
        scope: 'protected',
        schemaType: 'text',
        label: phoneNumberLabel,
        saveConfig: {
          label: phoneNumberLabel,
          isRequired: !!phoneNumberSettings.required,
          displayInSignUp: true,
        },
      });
    }
  }

  // Merge all fields, then group by signupStepGroup from the registry
  const allCustomFields = [...builtInFields, ...extendedDataFields];

  // Group fields by their registry signupStepGroup (fields without one go to 'default')
  const groupMap = new Map<string, any[]>();
  for (const field of allCustomFields) {
    const entry = getFieldRegistryEntry(field.key);
    const group = entry?.signupStepGroup || 'default';
    if (!groupMap.has(group)) groupMap.set(group, []);
    groupMap.get(group)!.push(field);
  }

  // Order groups: defined order first, then 'default' at the end
  const customFieldSteps: any[][] = [];
  for (const group of SIGNUP_STEP_GROUP_ORDER) {
    const fields = groupMap.get(group);
    if (fields?.length) customFieldSteps.push(fields);
  }
  const defaultFields = groupMap.get('default');
  if (defaultFields?.length) customFieldSteps.push(defaultFields);

  const user = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!user.id;
  const isLogin = tab === 'login';

  // Capture initial auth state on first render
  if (wasAuthenticatedOnMount.current === null) {
    wasAuthenticatedOnMount.current = isAuthenticated;
  }

  // If the user was already authenticated when the page loaded, redirect immediately.
  // If they just signed up (became authenticated during this session), wait for the wizard to finish.
  const canRedirect = isLogin || wasAuthenticatedOnMount.current || wizardComplete;

  const shouldRedirectToFrom = isAuthenticated && from && canRedirect;
  const shouldRedirectToLandingPage =
    isAuthenticated && currentUserLoaded && canRedirect;

  const handleWizardComplete = useCallback(() => {
    setWizardComplete(true);
  }, []);

  // Marketplace name and page metadata
  const marketplaceName = config.marketplaceName;
  const schemaTitle = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaTitleLogin' }, { marketplaceName })
    : intl.formatMessage({ id: 'AuthenticationPage.schemaTitleSignup' }, { marketplaceName });
  const schemaDescription = isLogin
    ? intl.formatMessage(
        { id: 'AuthenticationPage.schemaDescriptionLogin' },
        { marketplaceName }
      )
    : intl.formatMessage(
        { id: 'AuthenticationPage.schemaDescriptionSignup' },
        { marketplaceName }
      );

  // Show blank page while SSR hydration settles for authenticated users
  if (!mounted && shouldRedirectToLandingPage) {
    return (
      <Page title={schemaTitle} scrollingDisabled={scrollingDisabled}>
        <LayoutSingleColumn
          topbar={<TopbarContainerStyled />}
          footer={<FooterStyled />}
        >
          <div className="flex min-h-[60vh] items-center justify-center">
            <IconSpinner />
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  if (shouldRedirectToFrom) {
    return <Redirect to={from} />;
  } else if (shouldRedirectToLandingPage) {
    // Route users to their type-specific dashboard
    const resolvedUserType =
      selectedUserType ||
      user?.attributes?.profile?.publicData?.userType;
    if (resolvedUserType === 'provider' || resolvedUserType === 'vendor') {
      return <NamedRedirect name="VendorDashboardPage" />;
    }
    return <NamedRedirect name="DashboardPage" />;
  }

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainerStyled />}
        footer={<FooterStyled />}
      >
        <AuthLayout
          tab={tab}
          marketplaceName={marketplaceName}
          userType={preselectedUserType}
          brandImage={config.branding.brandImage}
        >
          {isLogin ? (
            <LoginCard
              onSubmit={submitLogin}
              loginError={loginError}
              inProgress={authInProgress}
            />
          ) : (
            <SignupWizard
              onSubmitSignup={submitSignup}
              signupError={signupError}
              inProgress={authInProgress}
              userType={selectedUserType}
              userTypes={userTypes}
              onUserTypeChange={setSelectedUserType}
              userTypeLocked={!!preselectedUserType}
              customFieldSteps={customFieldSteps}
              onComplete={handleWizardComplete}
            />
          )}
        </AuthLayout>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = (state: any) => {
  const { isAuthenticated, loginError, signupError } = state.auth;
  const { currentUser } = state.user;

  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    isAuthenticated,
    loginError,
    signupError,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  submitLogin: ({ email, password }: { email: string; password: string }) =>
    dispatch(login(email, password)),
  submitSignup: (params: any) => dispatch(signup(params)),
});

const NewAuthPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(NewAuthPageComponent);

export default NewAuthPage;
